import { useState } from "react";
import { Button, IconButton, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { RiDeleteBinLine } from "react-icons/ri";
import useThresholdSettings from "../../hooks/useThresholdSettings";
import Spinner from "../../components/spinner";
import DeleteModal from "../../components/elements/deleteModal";
import SwitchToggle from "../../components/elements/switchtoggle/SwitchToggle";
import { onNumberKeydown, onNumberFieldPaste } from "../../utilities";

const ThresholdSettings = () => {
  const {
    b2bRows,
    b2cRows,
    b2bStatus,
    b2cStatus,
    editingId,
    setEditingId,
    savingId,
    setSavingId,
    addRow,
    updateRowField,
    saveThreshold,
    deleteThreshold,
    updateThresholdStatus,
    updateSettingsTypeStatus,
    cancelEditRow,
    isLoading,
  } = useThresholdSettings();

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [thresholdToDelete, setThresholdToDelete] = useState(null);

  const handleDeleteClick = (thresholdId) => {
    setThresholdToDelete(thresholdId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteThreshold({ cart_threshold_id: thresholdToDelete });
    setDeleteModalOpen(false);
    setThresholdToDelete(null);
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="px-4">
      <div className="category__head headingBorder__b mb-8">
        <h2 className="titleHeading">Threshold Settings</h2>
      </div>

      <div className="space-y-12">
        {/* B2B Thresholds */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-700">B2B Thresholds</h3>
            <SwitchToggle
              label=""
              checkedValue={b2bStatus}
              onChange={(e) =>
                updateSettingsTypeStatus({
                  cart_settings_type: "B2B",
                  status: e.target.checked,
                })
              }
            />
          </div>
          <ThresholdTable
            data={b2bRows}
            type="B2B"
            editingId={editingId}
            setEditingId={setEditingId}
            savingId={savingId}
            setSavingId={setSavingId}
            onAddRow={addRow}
            onUpdateField={updateRowField}
            onSaveThreshold={saveThreshold}
            onDeleteThreshold={handleDeleteClick}
            onUpdateStatus={updateThresholdStatus}
            cancelEditRow={cancelEditRow}
          />
        </div>

        {/* B2C Thresholds */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-700">B2C Thresholds</h3>
            <SwitchToggle
              label=""
              checkedValue={b2cStatus}
              onChange={(e) =>
                updateSettingsTypeStatus({
                  cart_settings_type: "B2C",
                  status: e.target.checked,
                })
              }
            />
          </div>
          <ThresholdTable
            data={b2cRows}
            type="B2C"
            editingId={editingId}
            setEditingId={setEditingId}
            savingId={savingId}
            setSavingId={setSavingId}
            onAddRow={addRow}
            onUpdateField={updateRowField}
            onSaveThreshold={saveThreshold}
            onDeleteThreshold={handleDeleteClick}
            onUpdateStatus={updateThresholdStatus}
            cancelEditRow={cancelEditRow}
          />
        </div>
      </div>

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title={"Delete Threshold"}
        message={"Are you sure you want to delete this threshold?"}
        onDelete={handleConfirmDelete}
      />
    </div>
  );
};

const ThresholdTable = ({
  data,
  type,
  editingId,
  setEditingId,
  savingId,
  setSavingId,
  onAddRow,
  onUpdateField,
  onSaveThreshold,
  onDeleteThreshold,
  onUpdateStatus,
  cancelEditRow,
}) => {
  const handleSaveClick = (index) => {
    onSaveThreshold(type, index);
  };

  const handleCancelClick = (index, row) => {
    if (row.cart_threshold_id == null) {
      cancelEditRow(type, index);
    } else {
      setEditingId(null);
    }
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => onAddRow(type)}
          className="primaryBtn-outlined"
        >
          Add Threshold
        </Button>
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left bg-gray-100">
            <th className="sticky top-0 border-b border-gray-200 px-6 py-3 text-gray-600 font-bold tracking-wider uppercase text-xs">
              Cart Value
            </th>
            <th className="sticky top-0 border-b border-gray-200 px-6 py-3 text-gray-600 font-bold tracking-wider uppercase text-xs">
              Percentage
            </th>
            <th className="sticky top-0 border-b border-gray-200 px-6 py-3 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
              Status
            </th>
            <th className="sticky top-0 border-b border-gray-200 px-6 py-3 text-gray-600 font-bold tracking-wider uppercase text-xs text-center min-w-[220px]">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center py-8 text-gray-500">
                No Data Found
              </td>
            </tr>
          ) : (
            data.map((row, index) => {
              const isEditing =
                editingId === row.cart_threshold_id ||
                row.cart_threshold_id === null;
              const isSaving =
                savingId === (row.cart_threshold_id || `temp-${index}`);

              return (
                <tr key={index} className="border-b border-gray-200">
                  <td className="px-6 py-4">
                    <input
                      type="number"
                      value={row.threshold}
                      onChange={(e) =>
                        onUpdateField(
                          type,
                          index,
                          "threshold",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      placeholder="Enter value"
                      className={`input w-full ${
                        !isEditing ? "bg-gray-100" : ""
                      }`}
                      onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                      onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="number"
                      value={row.cart_threshold_value}
                      onChange={(e) =>
                        onUpdateField(
                          type,
                          index,
                          "cart_threshold_value",
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      placeholder="Enter percentage"
                      className={`input w-full ${
                        !isEditing ? "bg-gray-100" : ""
                      }`}
                      onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                      onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      {row.cart_threshold_id == null ? (
                        <span className="ml-6">--</span>
                      ) : (
                        <SwitchToggle
                          label=""
                          checkedValue={
                            row.status === true ||
                            row.status === "1" ||
                            row.status === 1
                          }
                          onChange={(e) =>
                            onUpdateStatus({
                              cart_threshold_id: row.cart_threshold_id,
                              status: e.target.checked,
                            })
                          }
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center min-w-[220px]">
                    <div className="flex items-center justify-center gap-2">
                      {!isEditing ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setEditingId(
                                editingId === row.cart_threshold_id
                                  ? null
                                  : row.cart_threshold_id
                              )
                            }
                          >
                            <EditIcon className="text-blue-600" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onDeleteThreshold(row.cart_threshold_id)
                            }
                          >
                            <RiDeleteBinLine className="text-lg text-red-600" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleSaveClick(index)}
                            disabled={isSaving}
                            className="relative"
                          >
                            {isSaving ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              "Save"
                            )}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCancelClick(index, row)}
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ThresholdSettings;